import { useState } from 'react';
import { API_BASE, setAuthToken, setSessionSecret, takeLoginNotice } from '../lib/api.jsx';
import { QrCode } from '../components/QrCode.jsx';
import { useTheme } from '../lib/theme.jsx';

const inputCls =
  'w-full px-3.5 py-2.5 mb-4 bg-sidebar border border-rule rounded-lg text-fg-2 text-sm outline-none box-border focus:border-blue-500';

export function LoginScreen({ onLogin }) {
  const [theme] = useTheme();
  const brandLogo =
    theme === 'light'
      ? '/branding/fonxtio/Logo_couleur_fonce-12.svg'
      : '/branding/fonxtio/Logo_couleur_pale_telipso.svg';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [need2FA, setNeed2FA] = useState(false);
  // Inscription 2FA obligatoire : jeton de 15 min, secret et QR de `2fa/setup`.
  const [enroll, setEnroll] = useState(null); // { token, secret, qrUri }
  const [error, setError] = useState('');
  const [notice] = useState(() => takeLoginNotice());
  const [loading, setLoading] = useState(false);

  const finishLogin = (data) => {
    setAuthToken(data.token);
    setSessionSecret(data.sessionToken || '');
    onLogin(data.user);
  };

  const backToPassword = () => {
    setNeed2FA(false);
    setEnroll(null);
    setTotpCode('');
    setError('');
  };

  const startEnrollment = async (enrollmentToken) => {
    const res = await fetch(API_BASE + '/auth/2fa/setup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + enrollmentToken },
      body: '{}',
    });
    if (!res.ok) throw new Error('setup');
    const d = await res.json();
    setEnroll({ token: enrollmentToken, secret: d.secret, qrUri: d.qrUri });
    setTotpCode('');
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const body = { email, password };
      if (need2FA && totpCode) body.totpCode = totpCode;
      const res = await fetch(API_BASE + '/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 429) {
        setError('Trop de tentatives. Réessayez plus tard.');
      } else {
        const data = await res.json();
        if (data.twoFactorRequired) {
          setNeed2FA(true);
          setError('');
        } else if (data.enrollmentRequired && data.enrollmentToken) {
          await startEnrollment(data.enrollmentToken);
        } else if (data.success) {
          finishLogin(data);
        } else {
          setError(need2FA ? 'Code invalide, réessayez.' : data.error || 'Identifiants invalides');
          if (need2FA) setTotpCode('');
        }
      }
    } catch (e) {
      setError('Erreur de connexion au serveur');
    }
    setLoading(false);
  };

  // Activation : code valide ⇒ 2FA activé ET connexion complète (jeton + session).
  const handleEnable = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API_BASE + '/auth/2fa/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + enroll.token },
        body: JSON.stringify({ secret: enroll.secret, code: totpCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        finishLogin(data);
        return;
      }
      if (res.status === 401 || res.status === 403) {
        backToPassword();
        setError("L'inscription a expiré : reconnectez-vous.");
      } else if (res.status === 429) {
        setError('Trop de tentatives. Réessayez plus tard.');
      } else {
        setError('Code invalide, réessayez.');
        setTotpCode('');
      }
    } catch (e) {
      setError('Erreur de connexion au serveur');
    }
    setLoading(false);
  };

  const codeStep = need2FA || enroll;
  const submit = enroll ? handleEnable : handleLogin;

  return (
    <div className="flex items-center justify-center min-h-screen w-full bg-app">
      <div className="bg-card border border-rule rounded-2xl p-9 w-[400px] max-w-[90%]">
        <div className="flex items-center gap-3 mb-7">
          <div className="w-[42px] h-[42px] rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-xl font-bold text-white">
            M
          </div>
          <div>
            <div className="text-[22px] font-bold text-fg font-mono">Fonxtio RMM</div>
            <div className="text-xs text-muted-2">Plateforme de surveillance</div>
          </div>
        </div>

        {notice && !error && (
          <div className="text-warn text-[13px] mb-4 text-center p-3 bg-warn/10 rounded-lg border border-warn/20">
            {notice}
          </div>
        )}
        {error && <div className="text-crit text-xs mb-3 text-center">{error}</div>}

        {!codeStep && (
          <div>
            <label className="block text-xs text-muted mb-1.5">Email</label>
            <input
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
            <label className="block text-xs text-muted mb-1.5">Mot de passe</label>
            <input
              className={inputCls}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
          </div>
        )}

        {enroll && (
          <div>
            <div className="text-warn text-[13px] mb-4 text-center p-3 bg-warn/10 rounded-lg border border-warn/20">
              La vérification en deux étapes est obligatoire. Configurez-la pour accéder à la plateforme.
            </div>
            <div className="text-fg text-[13px] mb-3">1. Scannez ce QR code avec votre appli d'authentification :</div>
            <div className="text-center mb-3">
              <QrCode value={enroll.qrUri} />
            </div>
            <div className="text-muted text-[11px] mb-4 text-center" style={{ wordBreak: 'break-all' }}>
              {'Clé manuelle : '}
              <code className="text-violet-400">{enroll.secret}</code>
            </div>
            <div className="text-fg text-[13px] mb-2">2. Entrez le code à 6 chiffres :</div>
            <input
              className={`${inputCls} text-center text-[22px] font-mono tracking-[8px]`}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && totpCode.length === 6 && handleEnable()}
            />
          </div>
        )}

        {need2FA && !enroll && (
          <div>
            <div className="text-warn text-[13px] mb-4 text-center p-3 bg-warn/10 rounded-lg border border-warn/20">
              Verification 2FA requise. Entrez le code de votre appli d'authentification.
            </div>
            <label className="block text-xs text-muted mb-1.5">Code 2FA</label>
            <input
              className={`${inputCls} text-center text-[22px] font-mono tracking-[8px]`}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && totpCode.length === 6 && handleLogin()}
            />
          </div>
        )}

        <button
          className="w-full py-3 rounded-[9px] border-none bg-blue-500 text-white text-sm font-semibold cursor-pointer disabled:opacity-60 hover:bg-blue-600 transition-colors"
          onClick={submit}
          disabled={loading || (codeStep && totpCode.length !== 6)}
        >
          {loading ? 'Connexion...' : enroll ? 'Activer et se connecter' : need2FA ? 'Verifier' : 'Se connecter'}
        </button>

        {codeStep && (
          <button
            onClick={backToPassword}
            className="w-full py-3 rounded-[9px] bg-transparent border border-rule-3 text-muted text-sm font-semibold cursor-pointer mt-2 hover:bg-card-hi"
          >
            Retour
          </button>
        )}

        <div className="flex flex-col items-center mt-[22px] pt-[18px] border-t border-rule">
          <img src={brandLogo} alt="Fonxtio — propulsé par Telipso" className="h-9 opacity-90" />
        </div>
      </div>
    </div>
  );
}
